<template>
  <base-dialog
    v-model="openModal"
    :fullscreen="paymentUrl"
    :max-width="maxWidth"
    :max-height="1500"
    class="payment-handler-dialog"
  >
    <template #content>
      <div class="payment-content">
        <h1 v-if="!paymentUrl && !showContactUs" class="subscription-title">
          {{
            type === 'ai'
              ? $t('generics.buyAiCredits')
              : $t('subscription.chooseSubscription')
          }}
        </h1>

        <div
          class="currency-select-container"
          :class="{ ltr: locale !== 'he' }"
          v-if="!paymentUrl && !showContactUs"
        >
          <v-select
            v-model="selectedCurrency"
            :items="currencyOptions"
            item-title="text"
            item-value="value"
            density="compact"
            variant="outlined"
            class="currency-select"
          />
        </div>

        <v-row
          v-if="!paymentUrl && !showContactUs"
          class="subscription-options"
        >
          <v-col
            v-for="(item, index) in visibleSubscriptions"
            :key="item._id + index"
            class="subscription-card"
            :class="{ 'sale-card': !!item.saleTitle }"
            @click="selectSubscription(item._id)"
            v-ripple
          >
            <div v-if="item.saleTitle" class="sale-badge">
              {{ item.saleTitle }}
            </div>
            <span class="subscription-title-text">
              {{ getLocalizedTitle(item) }}
            </span>
            <span class="subscription-description">
              {{ getLocalizedDescription(item) }}
            </span>
            <span class="subscription-price">
              <template v-if="isIsrael">
                <span class="price-value">{{ item.price }}</span>
                <span class="currency">{{ $t('generics.ins') }}</span>
              </template>
              <template v-else>
                <span class="currency">$</span>
                <span class="price-value">{{ item.dollars }}</span>
              </template>
            </span>
          </v-col>
        </v-row>

        <div
          v-if="!paymentUrl && showContactUs"
          class="d-flex flex-column align-center justify-center py-10"
        >
          <v-icon size="100" color="primary" class="mb-5"
            >mdi-office-building</v-icon
          >
          <h2 class="contact-title mb-4 text-center">
            {{ $t('games.remoteCreditInformNoCredits') }}
          </h2>
          <p class="contact-subtitle mb-8 text-center text-medium-emphasis">
            {{ $t('generics.institutionContactMessage') }}
          </p>
          <v-btn
            :href="waLink"
            target="_blank"
            color="success"
            size="large"
            rounded="xl"
            prepend-icon="mdi-whatsapp"
            class="px-8"
          >
            {{ $t('contact.whatsapp') }}
          </v-btn>
        </div>

        <iframe
          v-if="paymentUrl"
          class="payment-iframe"
          :src="paymentUrl"
        ></iframe>
      </div>
    </template>
  </base-dialog>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSubscriptionStore } from '@/store/subscription'
import { useAuthStore } from '@/store/auth'
import { storeToRefs } from 'pinia'
import { startPayment, cancelPayment } from '@/api/pay.api'
import { trackFBEvent } from '@/composables/useLoadScripts'
import { detectIsrael } from '@/utils/detectIsrael'

const openModal = defineModel('modelValue', {
  type: Boolean,
})

const props = defineProps({
  type: {
    type: String,
    default: 'game', // 'game' | 'ai'
  },
})

const { locale, t } = useI18n()

const selectedSubscription = ref(null)
const paymentUrl = ref(null)
const paymentPublicId = ref(null)
const selectedCurrency = ref(null)
const currencyOptions = [
  { text: '$ USD', value: 'usd' },
  { text: '₪ ILS', value: 'ils' },
]

const authStore = useAuthStore()
const { isInstitution, isAdmin } = storeToRefs(authStore)

const subscriptionStore = useSubscriptionStore()
const { subscriptions: allSubscriptions } = storeToRefs(subscriptionStore)
const { getSubscriptions } = subscriptionStore

const subscriptions = computed(() => {
  return allSubscriptions.value.filter(s => (s.type || 'game') === props.type)
})

const showContactUs = computed(() => isInstitution.value && !isAdmin.value)

const waLink = computed(() => `https://wa.me/972523309773`)

const isIsrael = computed(() => selectedCurrency.value === 'ils')
const visibleSubscriptions = computed(() => {
  return subscriptions.value.filter(item => {
    if (selectedCurrency.value === 'usd') {
      return item.dollars && Number(item.dollars) > 0
    }
    if (selectedCurrency.value === 'ils') {
      return item.price && Number(item.price) > 0
    }
    return true
  })
})
const maxWidth = computed(() => (paymentUrl.value ? 900 : 780))

function getLocalizedTitle(item) {
  return locale.value === 'he'
    ? item.title
    : locale.value === 'en'
      ? item.titleEn || item.title
      : locale.value === 'ar'
        ? item.titleAr || item.title
        : item.title
}

function getLocalizedDescription(item) {
  return locale.value === 'he'
    ? item.description
    : locale.value === 'en'
      ? item.descriptionEn || item.description
      : locale.value === 'ar'
        ? item.descriptionAr || item.description
        : item.description
}

async function exitPayment() {
  paymentUrl.value = null
  localStorage.removeItem('paymentStarted')
  if (paymentPublicId.value) {
    await cancelPayment(paymentPublicId.value)
  }
}

async function selectSubscription(subscriptionType) {
  trackFBEvent({ eventType: 'InitiateCheckout' })
  localStorage.setItem('paymentStarted', 'true')
  const { token, url } = await startPayment(
    locale.value,
    subscriptionType,
    isIsrael.value
  )
  paymentPublicId.value = token
  paymentUrl.value = url
}

onMounted(async () => {
  await getSubscriptions()
  selectedCurrency.value = detectIsrael() ? 'ils' : 'usd'
})
</script>

<style scoped>
.subscription-title {
  font-size: 24px;
  font-weight: 500;
  line-height: 35.27px;
  text-align: center;
  margin: 0 auto;
  z-index: 2;
}

.subscription-options {
  display: flex;
  justify-content: center;
  margin-top: 30px;
  margin-bottom: 60px;
  margin-left: 7px;
  margin-right: 7px;
  gap: 19px;
}

.subscription-card {
  box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.11);
  min-height: 225px;
  width: 216px;
  max-width: 216px;
  cursor: pointer;
  position: relative;
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5px;
}

.sale-badge {
  position: absolute;
  min-width: 81px;
  height: 30px;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 13px;
  display: grid;
  place-items: center;
  font-size: 16px;
  font-weight: 500;
  background: linear-gradient(99.39deg, #d973fe 0%, #4a72ff 98.23%);
  box-shadow: 0px 4px 0px 0px rgba(0, 25, 128, 1);
  color: white;
}

.subscription-title-text {
  margin-top: 30px;
  font-size: 24px;
  font-weight: 500;
  line-height: 35.27px;
  text-align: center;
}

.subscription-description {
  margin-top: 30px;
  font-size: 16px;
  font-weight: 400;
  line-height: 23.52px;
  text-align: center;
  width: 150px;
  margin-bottom: 80px;
  white-space: pre-line;
}

.subscription-price {
  display: flex;
  align-items: center;
  margin-bottom: 3px;
  gap: 8px;
  position: absolute;
  bottom: 0;
}

.price-value {
  font-size: 32px;
  font-weight: 500;
  line-height: 47.03px;
}

.currency {
  font-size: 16px;
  font-weight: 500;
  line-height: 23.52px;
  margin-top: 8px;
}

.payment-iframe {
  border: none;
  width: 100%;
  height: 88vh;
}

.sale-card {
  background:
    linear-gradient(#ffffff 0 0) padding-box,
    var(--linearHotPinkDodgerBlue) border-box;
  border: 3px solid transparent;
}

.currency-select-container {
  position: absolute;
  right: 15px;
  top: 15px;
  z-index: 2;
  width: 120px;
}
.ltr.currency-select-container {
  right: unset;
  left: 15px;
}

.currency-select {
  min-width: 100px;
}

.payment-content {
  max-height: 76vh;
  padding-bottom: 24px;
}

.contact-title {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
}

.contact-subtitle {
  font-size: 20px;
  line-height: 1.4;
}
</style>
